import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { BellRing, Send, SmartphoneNfc, RotateCcw } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import api from "../services/api";

// Push delivery observability.
//
// Two rates, and the whole value of this page is that it keeps them apart:
//   • "Accepted by provider" — FCM/web-push took our send. A low rate here is
//     OUR problem (stale tokens, bad credentials).
//   • "Reached device" — the handset confirmed it actually surfaced the push.
//     A low rate here with a healthy accept rate is the DEVICE: an OEM battery
//     manager force-stopping the app, or a muted notification channel.
//
// Before this existed, both failure modes looked identical: a user saying "I
// didn't get it".

const WINDOWS = [
  { days: 1, label: "24 hours" },
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
];

const HEALTH_STYLES = {
  good: { label: "Healthy", className: "bg-green-100 text-green-800" },
  degraded: { label: "Degraded", className: "bg-amber-100 text-amber-800" },
  bad: { label: "Failing", className: "bg-red-100 text-red-800" },
  "insufficient-data": {
    label: "Low volume",
    className: "bg-gray-100 text-gray-600",
  },
};

const rateColor = (value) => {
  if (value >= 80) return "text-green-600";
  if (value >= 50) return "text-amber-600";
  return "text-red-600";
};

const StatCard = (props) => {
  // Assigned to a capitalized local rather than destructured in the signature:
  // this eslint config has no react plugin, so JSX usage only suppresses
  // no-unused-vars via varsIgnorePattern '^[A-Z_]', which doesn't cover params.
  const Icon = props.icon;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
        <Icon size={16} />
        <span className="text-sm">{props.label}</span>
      </div>
      <p
        className={`mt-2 text-2xl font-semibold ${props.valueClassName ?? "text-gray-900 dark:text-white"}`}
      >
        {props.value}
      </p>
      {props.sub && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {props.sub}
        </p>
      )}
    </div>
  );
};

const NotificationDelivery = () => {
  const [report, setReport] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/v1/notifications/delivery-report", {
        params: { days },
      });
      setReport(response.data);
    } catch (error) {
      console.error("Failed to load delivery report:", error);
      toast.error(
        error?.response?.data?.message || "Could not load delivery report",
      );
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const summary = report?.summary;
  const hasData = !!summary && summary.sent > 0;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Push delivery
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Whether notifications actually reach riders and drivers — and
                when they don&apos;t, whose fault it is.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {WINDOWS.map((window) => (
                <button
                  key={window.days}
                  onClick={() => setDays(window.days)}
                  className={`rounded-lg px-3 py-1.5 text-sm ${
                    days === window.days
                      ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                      : "border border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300"
                  }`}
                >
                  {window.label}
                </button>
              ))}
              <button
                onClick={loadReport}
                className="rounded-lg border border-gray-300 p-2 text-gray-600 dark:border-gray-600 dark:text-gray-300"
                title="Refresh"
              >
                <RotateCcw size={16} />
              </button>
            </div>
          </div>

          {loading && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Loading delivery report…
            </p>
          )}

          {!loading && !hasData && (
            <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                No pushes recorded in this window. If the apps have just been
                updated, delivery data appears as soon as notifications start
                going out — each send is recorded, and each device confirms
                receipt.
              </p>
            </div>
          )}

          {!loading && hasData && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <StatCard
                  icon={Send}
                  label="Sent"
                  value={summary.sent.toLocaleString()}
                  sub="Send attempts recorded"
                />
                <StatCard
                  icon={BellRing}
                  label="Accepted by provider"
                  value={`${summary.sendSuccessRatePct}%`}
                  valueClassName={rateColor(summary.sendSuccessRatePct)}
                  sub={`${summary.accepted.toLocaleString()} of ${summary.sent.toLocaleString()} · low = our problem`}
                />
                <StatCard
                  icon={SmartphoneNfc}
                  label="Reached device"
                  value={`${summary.deliveryRatePct}%`}
                  valueClassName={rateColor(summary.deliveryRatePct)}
                  sub={`${summary.acknowledged.toLocaleString()} confirmed · low = device or channel`}
                />
              </div>

              <section className="mt-8">
                <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                  By device model
                </h2>
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
                      <tr>
                        <th className="px-4 py-3 font-medium">Device</th>
                        <th className="px-4 py-3 font-medium">Platform</th>
                        <th className="px-4 py-3 font-medium">Sent</th>
                        <th className="px-4 py-3 font-medium">Accepted</th>
                        <th className="px-4 py-3 font-medium">Reached</th>
                        <th className="px-4 py-3 font-medium">Health</th>
                        <th className="px-4 py-3 font-medium">
                          Most likely cause
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {report.byDevice.map((row) => {
                        const health =
                          HEALTH_STYLES[row.health] ??
                          HEALTH_STYLES["insufficient-data"];
                        return (
                          <tr
                            key={`${row.deviceModel}-${row.platform}`}
                            className="text-gray-700 dark:text-gray-200"
                          >
                            <td className="px-4 py-3 font-medium">
                              {row.deviceModel}
                            </td>
                            <td className="px-4 py-3">{row.platform}</td>
                            <td className="px-4 py-3">{row.sent}</td>
                            <td className="px-4 py-3">
                              {row.sendSuccessRatePct}%
                            </td>
                            <td
                              className={`px-4 py-3 font-medium ${rateColor(row.deliveryRatePct)}`}
                            >
                              {row.deliveryRatePct}%
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`rounded-full px-2 py-1 text-xs font-medium ${health.className}`}
                              >
                                {health.label}
                              </span>
                            </td>
                            <td className="max-w-md px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                              {row.diagnosis}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
                <section>
                  <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                    By platform
                  </h2>
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-left text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
                        <tr>
                          <th className="px-4 py-3 font-medium">Platform</th>
                          <th className="px-4 py-3 font-medium">Sent</th>
                          <th className="px-4 py-3 font-medium">Reached</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {report.byPlatform.map((row) => (
                          <tr
                            key={row.platform}
                            className="text-gray-700 dark:text-gray-200"
                          >
                            <td className="px-4 py-3">{row.platform}</td>
                            <td className="px-4 py-3">{row.sent}</td>
                            <td
                              className={`px-4 py-3 font-medium ${rateColor(row.deliveryRatePct)}`}
                            >
                              {row.deliveryRatePct}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section>
                  <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                    By channel
                  </h2>
                  <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                    A low rate on <code>ride-requests-v2</code> specifically
                    points at that channel being muted rather than a
                    device-wide problem.
                  </p>
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-left text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
                        <tr>
                          <th className="px-4 py-3 font-medium">Channel</th>
                          <th className="px-4 py-3 font-medium">Sent</th>
                          <th className="px-4 py-3 font-medium">Reached</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {report.byChannel.map((row) => (
                          <tr
                            key={row.channelId}
                            className="text-gray-700 dark:text-gray-200"
                          >
                            <td className="px-4 py-3">{row.channelId}</td>
                            <td className="px-4 py-3">{row.sent}</td>
                            <td
                              className={`px-4 py-3 font-medium ${rateColor(row.deliveryRatePct)}`}
                            >
                              {row.deliveryRatePct}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>

              <p className="mt-6 text-xs text-gray-500 dark:text-gray-400">
                Note: on Android, a push carrying a notification block that
                arrives while the app is backgrounded is rendered by the OS
                without running app code, so it can only be confirmed when the
                user taps it. Read &ldquo;Reached device&rdquo; as{" "}
                <em>reached the app or the user</em>. Ride requests are sent
                data-only and confirm without a tap, which makes the
                ride-request numbers the most reliable signal here.
              </p>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default NotificationDelivery;
